<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import { storeToRefs } from "pinia";
import { computed, nextTick, onBeforeUnmount, ref, watch, watchEffect } from "vue";
import PlusOrange from "#/assets/iconsDynamic/PlusOrange.vue";
import { usePdf } from "#/composables/usePdfMode";
import { vDragScroll } from "#/directives/vDragScroll";
import UseScrollCenter from "#/hooks/useScrollToCenter/UseScrollCenter.vue";
import { useCardStore } from "#/store/card";
import { useThemeStore } from "#/store/theme";
import type { IGroupModifierSelected, IGroupModifiers } from "#/types/iCardProduct";
import { getRegularModifierGroups, isSwiperModifierGroup } from "#/utils/customRenderModifiers";
import { getLocaleName } from "#/utils/i18next";

defineProps<{
	isSectionModifier: boolean;
	toggleNotRequire: (group: IGroupModifiers, index: number) => void;
}>();

const { i18next } = useTranslation();
const cardStore = useCardStore();
const themeStore = useThemeStore();
const { stateCard } = storeToRefs(cardStore);
const { shouldUseDragScroll } = usePdf();

const modifiedPlusBtnRefs = ref<HTMLElement[]>([]);
const groups = ref<IGroupModifierSelected[]>([]);
const regularGroups = computed(() => getRegularModifierGroups(stateCard.value.groupModifiers));

const scrollPlusContainer = ref<HTMLElement | null>(null);
const scrollCenterRef = ref<InstanceType<typeof UseScrollCenter> | null>(null);
const activeId = ref<string | null>(null);

const activeIdSelect = (id: string) => {
	activeId.value = id;
};

watchEffect(() => {
	groups.value = stateCard.value.groupModifiersSelected;
});

watch(
	() => stateCard.value.groupCurrentOpen?.id,
	async (id) => {
		if (!id) return;
		await nextTick();
		const el = modifiedPlusBtnRefs.value.find((el) => el.id === "scrolling" + id);
		if (el) scrollCenterRef.value?.scrollToElement(el);
	},
);

onBeforeUnmount(() => {
	modifiedPlusBtnRefs.value = [];
});
</script>

<template>
	<div :class="{'card_modified_wrapper_many': regularGroups.length >= 5}">
		<div
			class="card_modified_wrapper"
			v-drag-scroll="shouldUseDragScroll ? {} : false"
			:class="[regularGroups.length <= 4 ? 'card_modified_wrapper_center': '']"
			ref="scrollPlusContainer"
			v-if="stateCard.groupModifiersSelected"
		>
			<UseScrollCenter
				ref="scrollCenterRef"
				axis="x"
				behavior="smooth"
				:container="scrollPlusContainer"
				:autoScroll="true"
				:idActive="activeId"
				:clearContainer="true"
			>
				<template #default="{ handleTargetClick }">
					<template v-for="(itemGroup, index) in stateCard.groupModifiers" :key="itemGroup.id">
					<div
						v-if="!isSwiperModifierGroup(itemGroup)"
						class="button_modified"
						:id="'scrolling' + itemGroup.id"
						data-clickable="true"
						@click="(e: any) => { 
            if(toggleNotRequire) toggleNotRequire(itemGroup, index); 
            activeIdSelect(itemGroup.id);
            handleTargetClick(e); 
          }"
					>
						<div class="modified_plus_btn">
							<div
								v-if="groups[index].modifiers.length === 0"
								:class="{ 'modified_plus_btn_active': itemGroup.id === stateCard.groupCurrentOpen?.id && isSectionModifier}"
								class="modified_plus_active"
							>
								<PlusOrange />
							</div>
							<div
								v-else-if="groups[index].modifiers.length === 1"
								class="modified_plus_btn modified_plus_btn_solo"
								:class="{ 'modified_plus_btn_active': itemGroup.id === stateCard.groupCurrentOpen?.id && isSectionModifier}"
							>
								<img :src="groups[index].modifiers[0].image_link || themeStore.data?.fallback_image || ''" :alt="itemGroup.name">
							</div>
							<div class="wrapper_modifier_item" v-else>
								<div class="modifier_item_n_wrapper" v-if="groups[index].modifiers.length > 2">
									<img
										class="modifier_plus_item modifier_item_three"
										v-if="groups[index].modifiers.length === 3"
										:src="groups[index].modifiers[2].image_link || themeStore.data?.fallback_image || ''"
										alt=""
									>
									<p v-else class="modifier_plus_item">+ {{ groups[index].modifiers.length - 2 }}</p>
								</div>
								<div>
									<img
										v-if="groups[index].modifiers.length > 0"
										:src="groups[index].modifiers[0].image_link || themeStore.data?.fallback_image || ''"
										class="modifier_plus_item modifier_item_first"
										alt=""
									>
									<img
										v-if="groups[index].modifiers.length > 1"
										:src="groups[index].modifiers[1].image_link || themeStore.data?.fallback_image || ''"
										class="modifier_plus_item modifier_item_second"
										alt=""
									>
								</div>
							</div>
						</div>
						<p>{{ getLocaleName(itemGroup, i18next.language) }}</p>
					</div>
					</template>
				</template>
			</UseScrollCenter>
		</div>
	</div>
</template>

<style scoped lang="scss">
  @use './plusModifier.scss'
</style>
